import { ReactNode, createContext, useCallback, useState, useContext, useEffect } from "react";
import { useLocalStorageState } from "../../hooks/localStorage";
import { ProjectID } from "../../types/project";

const colorPalette = ["#D18213", "#42362A", "#FBD6A7", "#604A31"];

export type ColorContext = ProjectID | "wellLevels" | "global";

export type SiteColorRecord = Record<string, { color: string; context: ColorContext }>;

export const SiteColorsContext = createContext<{
  getSiteColor: (siteName: string, context: ColorContext) => string;
  setSiteColor: (siteName: string, color: string) => void;
  sitesColors: SiteColorRecord;
}>({
  getSiteColor: () => "",
  setSiteColor: () => {},
  sitesColors: {},
});

export function SiteColorsProvider({ children }: { children: ReactNode }) {
  const [saved, handleUpdate] = useLocalStorageState({ key: "sitesColorsV2", value: {} });
  const [colors, setColors] = useState<SiteColorRecord>(saved);
  const getSiteColor = useCallback(
    (siteName: string, context: ColorContext) => {
      if (colors[siteName]) return colors[siteName].color;

      const availableColors = colorPalette.filter(
        (color) => !Object.values(colors).some((c) => c.color === color && c.context === context),
      );
      const color =
        availableColors.length > 0 ? availableColors[0] : "#" + ((Math.random() * 0xffffff) << 0).toString(16);
      setColors((prevColors) => {
        if (Object.values(prevColors).some((c) => c.color === color && c.context === context)) return prevColors;
        return { ...prevColors, [siteName]: { color, context } };
      });
      return color;
    },
    [colors],
  );
  const setSiteColor = useCallback(
    (siteName: string, color: string) => {
      setColors((prevColors) => {
        handleUpdate({ ...saved, [siteName]: { ...prevColors[siteName], color: color.toUpperCase() } });

        return {
          ...prevColors,
          [siteName]: { ...prevColors[siteName], color: color.toUpperCase() },
        };
      });
    },
    [saved, handleUpdate],
  );

  return (
    <SiteColorsContext.Provider value={{ getSiteColor, setSiteColor, sitesColors: colors }}>
      {children}
    </SiteColorsContext.Provider>
  );
}

export function useSitesColors(sites: string[], context: ColorContext) {
  const { getSiteColor } = useContext(SiteColorsContext);
  const [sitesColor, setSitesColor] = useState<Record<string, string>>({});
  useEffect(() => {
    setSitesColor(Object.fromEntries(sites.map((site) => [site, getSiteColor(site, context)])));
  }, [sites, context, getSiteColor]);

  return sitesColor;
}

export function useSiteColor(site: string, context: ColorContext) {
  const { getSiteColor } = useContext(SiteColorsContext);
  const [siteColor, setSiteColor] = useState<string>("");
  useEffect(() => {
    setSiteColor(getSiteColor(site, context));
  }, [site, context, getSiteColor]);

  return siteColor;
}
